import * as React from 'react';
import axios from 'axios';
import { Stack, MessageBarType, SearchBox } from '@fluentui/react';
import { DataGrid, GridPagination, GridColumnMenu } from '@mui/x-data-grid';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { useHistory } from 'react-router-dom';
import { THEME_ID, dataGridsx, handleTextTheme, searchInputStyles } from '../../shared/styles';
import { Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiPagination from '@mui/material/Pagination';
import { saveAttachment, downloadAttachmentLocally } from 'src/taskpane/pages/tasks/SaveAttachment';
import { parentStackStyles, stackTokens, HeaderMargin } from '../../utility/tasks/styles';
import {
	ACTION_ROLE,
	ACTION_TYPE,
	CMIC_FIELD_REST__NAMES,
	CMIC_TREE_NODE_TYPES,
	DOC_PAGE_LIMIT,
	PAGE_LIMIT,
	SPLIT_SIZES,
	APP_NAME,
} from '../../shared/Constants';
import { ActionContext } from '../../context/ActionContext';
import { CmicContext } from '../../context/CmicContext';
import { COLORS } from '../../utility/tasks/colors';
import { filterFormatter } from '../../utility/grid/helper/FliterHelper';
import { isShowMetaPanelData } from '../metaDataPanel/metaDataPanel';
import { handleUploadAttsToExistingPMItems } from './upload/CreateDocumentRecord';
import { handleSortFormat } from '../../utility/grid/helper/SortHelper';
import { handleSearchBarVisibility, handlePageSize, isBGClrDark } from '../../utility/UtilityFunctions';
import { dataGridStyling, baseFontFamilyStyle } from '../../shared/styles';
import ActionUploadAttachment from './upload/ActionUploadAttachment';
import BorderIcon from '../../components/BorderIcon';
import CustomBreadCrumbs from '../../components/CustomBreadcrumbs';
import CreateActions from './CreateActions';
import definedGridColumns from '../../utility/grid/GridColumns';
import definedGridRows from '../../utility/grid/GridRows';
import PMForm from './PMForm';
import { segmentTrackCall, segmentDefaultPageCall } from '../../utility/segment/segmentUtility';
import { isInternetAvailable } from '../../shared/UtilityFunctions';

// Styles
const loaderStyles = {
	alignItems: 'center',
	display: 'flex',
	justifyContent: 'center',
	flex: 1,
	height: '100%',
};

const ProjListAction = () => {
	const { useCallback, useContext, useEffect, useState, useRef } = React;

	const navigate = useHistory();

	let segmentDocObject = [];

	const { userInfo, currentOfficeTheme, selectedProject, setShowToast } = useContext(CmicContext);
	const {
		nodeData = { items: [], totalResults: 0, hasMore: false, statusData: [] },
		activeNode,
		breadcrumbs,
		actionTypeData,
		docDataForUpload,
		selectedImagingDoc,
		attachmentData,
		pageInfo,
		sortModel,
		isLoading,
		handleDataFetch,
		handleParentCountUpdate,
		setAttachmentData,
		setSelectedAttachmentsIds,
		setItemFromEmail,
		setSortModel,
		fetchDataPagination,
		handleImagingBackPress,
		setIsLoading,
	} = useContext(ActionContext);

	const [rows, setRows] = useState([]);
	const [columns, setColumns] = useState([]);
	const [splitSize, setSplitSize] = useState(SPLIT_SIZES.MIN);
	const [attachmentSize, setAttachmentSize] = useState(SPLIT_SIZES.CREATE);
	const [pageSize, setPageSize] = useState(PAGE_LIMIT);
	const [isSearchPMVisible, setIsSearchPMVisible] = useState(false);
	const [showAttachments, setShowAttachments] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const isDoubleClicked = useRef(null);
	const isNodeClicked = useRef(null);
	const searchTimer = useRef(null);
	const [activeNodeIndex, setActiveNodeIndex] = useState(-1);

	useEffect(() => {
		let item = Office?.context?.mailbox?.item || null;

		if (!item) return;

		let obj = JSON.parse(
			JSON.stringify({
				messageId: item?.itemId || '-',
				attachments: [
					...item.attachments.map(data => {
						let newData = { ...data, originalName: data.name };
						return newData;
					}),
				],
				name: item.subject,
				from: item.from.displayName,
				dateTimeCreated: item.dateTimeCreated,
			}),
		);

		setItemFromEmail(obj);

		return () => {
			// Clearing Timeouts if any
			clearTimeout(isNodeClicked.current);
			clearTimeout(isDoubleClicked.current);
			clearTimeout(searchTimer.current);
			isNodeClicked.current = null;
			isDoubleClicked.current = null;
			searchTimer.current = null;
		};
	}, []);

	useEffect(() => {
		if (!activeNode) return;
		if (actionTypeData.type === ACTION_TYPE.NEW) {
			handleNewAttachmentContainerSize(SPLIT_SIZES.CREATE);
		} else {
			const visiblityCheck = handleAttachmentVisiblity();
			const requiredSize = handlePageSize(activeNode);
			setShowAttachments(visiblityCheck.isAttachmentDocVisible);
			setSplitSize(visiblityCheck.size);
			setPageSize(requiredSize);
			setRows(definedGridRows(activeNode, nodeData?.items || []));
		}
		setAttachmentData([]);
		setSelectedAttachmentsIds({});

		// Added logic to prevent other sorting filtering and paging funcs to be called un necessarily
		setTimeout(() => {
			isNodeClicked.current = false;
		}, [1000]);
	}, [activeNode, nodeData]);

	useEffect(() => {
		if (!activeNode) return;
		setColumns(definedGridColumns(activeNode, nodeData.statusData));

		setIsSearchPMVisible(handleSearchBarVisibility(activeNode));
	}, [activeNode]);

	const handleNodeClick = (node, index) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		if (!node) return;
		setActiveNodeIndex(index);
		isNodeClicked.current = true;
		// Adding delay to handle single and double click efficiently - Jitesh
		if (isDoubleClicked.current) return;

		isDoubleClicked.current = setTimeout(() => {
			handleDataFetch(node, nodeData?.items[index]);

			clearTimeout(isDoubleClicked.current);
			isDoubleClicked.current = null;
		}, 500);

		// Add a segment PAGE call when the user clicks on any RFI / ISSUE/ JOURNAL/ SUBMITTAL and drills down into it.
		if (userInfo.actionRole === ACTION_ROLE.PROJECT && userInfo?.isCloudUser) {
			if (nodeData?.items[index]) {
				const obj = nodeData?.items[index];

				const segmentObj = {
					appName: APP_NAME,
					contactCode: userInfo?.info?.contactCode,
					timeStamp: new Date(),
					projectName: selectedProject?.PmpProjName,
					projectId: selectedProject?.PmpVUuid,
				};

				let pageName = 'EXISTING_RFI';

				if (node === CMIC_TREE_NODE_TYPES.PROJECT_RFI) {
					segmentObj.RFIId = obj.PmscrfiRfiId;
				} else if (node === CMIC_TREE_NODE_TYPES.PROJECT_ISSUE) {
					segmentObj.issueId = obj?.DmiIssueId;
					pageName = 'EXISTING_ISSUE';
				} else if (node === CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL) {
					segmentObj.journalId = obj?.PmjJournalId;
					pageName = 'EXISTING_JOURNAL';
				} else if (node === CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS) {
					segmentObj.submittalId = obj?.PmsmSbmtId;
					pageName = 'EXISTING_SUBMITTAL';
				}
				segmentDefaultPageCall(pageName, segmentObj);
			}
		}
	};

	const handleBackIconClick = () => {
		if (isLoading) return;
		setActiveNodeIndex(-1);
		if (userInfo.actionRole === ACTION_ROLE.ECM)
			handleImagingBackPress(CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT);
		else {
			navigate.replace('/home');
		}
	};

	const handleAttachmentVisiblity = status => {
		if (status === 'NEW')
			return {
				isAttachmentDocVisible: false,
				size: SPLIT_SIZES.MIN,
			};

		switch (activeNode) {
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_COMMUNICATION:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_DAILY_JOURNAL:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_DOCUMENTS:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_ISSUE:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_PCI:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_RFI:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_SUBCONTRACTORS:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_SUBCONTRACTORS_CHANGE_ORDERS:
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_SUBMITTALS:
			case CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT:
				return {
					isAttachmentDocVisible: false,
					size: SPLIT_SIZES.MIN,
				};

			case CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT:
				return {
					isAttachmentDocVisible: false,
					size: SPLIT_SIZES.MIN,
				};

			default:
				return {
					isAttachmentDocVisible: true,
					size: SPLIT_SIZES.MIN,
				};
		}
	};

	const handleClickScroll = () => {
		window.setTimeout(() => {
			const element = document.getElementById('section-1');
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}, 10);
	};

	const handlePMItemSearch = event => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
			searchTimer.current = null;
		}

		searchTimer.current = setTimeout(() => {
			fetchDataPagination(activeNode, { num: 0, search: event?.target?.value || '' });
			clearTimeout(searchTimer.current);
			searchTimer.current = null;
		}, 800);
	};

	const handleECMAttachmentsBtnPress = async (attachments, mailInfo) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		setIsLoading(true);
		if (attachments.length === 0) {
			setShowToast({
				isVisible: true,
				msg: `Please select atleast one attachment`,
				type: MessageBarType.info,
			});

			return;
		}
		setIsSubmitting(true);
		const failedAttachments = [];
		const docsData = [];
		try {
			// Fetching Attachments Info
			attachments.forEach(async attachment => {
				docsData.push(
					new Promise(async (resolve, reject) => {
						const result = await saveAttachment(attachment);
						resolve({ ...attachment, ...result });
					}),
				);
			});
		} catch (error) {
			console.log('handleECMAttachmentsBtnPress() ERROR', error);
			setIsSubmitting(false);
		}

		// Uploading docs
		Promise.all(docsData)
			.then(async docs => {
				for (let doc of docs) {
					let data = new FormData();
					let blobData = downloadAttachmentLocally(doc);
					data.append('document', blobData, doc.name);

					let headers = {};

					if (userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth) {
						headers.Authorization = `Bearer ${userInfo?.accessToken}`;
					}

					let config = {
						method: 'post',
						url: `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.uploadDocument}
								/${selectedImagingDoc.value}
								/${docDataForUpload[activeNode].nodeId
							}`,
						...(userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth
							? {}
							: {
								auth: {
									username: userInfo.username,
									password: userInfo.password,
								},
							}),
						headers: headers,
						data: data,
					};

					try {
						const result = await axios.request(config);

						if (!result) throw new Error('DocUpload Failed');

						// Add document for the Segment TRACK call
						pushDoc('Success');
					} catch (error) {
						console.log('handleECMAttachmentsBtnPress() Error', error);
						// Add document for the Segment TRACK call
						pushDoc('Fail');
						failedAttachments.push(doc);
					}
				}
				//Reloading Data
				handleParentCountUpdate(docs.length - failedAttachments.length, () => handlePaginationModelChange(0), activeNodeIndex);
			})
			.catch(err => {
				console.log('Docs Error', err);
				pushDoc('Fail');
				setShowToast({
					isVisible: true,
					msg: `Upload failed!`,
					type: MessageBarType.error,
				});
			})
			.finally(() => {
				setIsSubmitting(false);
				// Triggering TRACK call for all documents
				const eventName = 'Imaging- File Upload';
				segmentTrackCall(eventName, userInfo, segmentDocObject);
				segmentDocObject = [];
			});
	};

	// For ECM document upload, we are creating an object for segment track call
	const pushDoc = (status, fileDetails = {}) => {
		let obj = {};
		const docDetails = docDataForUpload[activeNode];
		let isRevision = docDetails.ecmParentNodeType === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_REVISION;

		obj = {
			timeStamp: new Date(),
			contactCode: userInfo?.info?.contactCode,
			imagingSourceName: isRevision ? docDetails?.ImgdSourceDesc : docDetails?.ImgsSourceDesc,
			imagingSourceId: isRevision ? docDetails?.ImgsVUuid : docDetails?.ImgsdtSourceUuid,
			imagingDocumentTypeName: isRevision ? docDetails?.ImgdDocumentTypeDesc : docDetails?.ImgdtDocumentTypeDesc,
			imagingDocumentTypeId: docDetails?.ImgdtVUuid,
			uploadStatus: status,
			fileID: fileDetails?.imgDocUuid,
			fileName: fileDetails?.imgDocumentName,
			isRevision: isRevision ? 'Yes' : 'No',
			parentImagingDocumentName:
				isRevision && docDetails?.ImgdDocumentParameters ? docDetails?.ImgdDocumentParameters : '',
			parentImagingDocumentId: isRevision && docDetails?.ImgdVUuid ? docDetails?.ImgdVUuid : '',
		};
		segmentDocObject.push(obj);
	};

	const handleExistingPMAttachmentsBtnPress = (attachments, mailInfo) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		setIsSubmitting(true);
		const docsData = [];

		attachments.forEach(async result => {
			docsData.push(
				new Promise(async (resolve, reject) => {
					const response = await saveAttachment(result);
					resolve({ ...result, ...response });
				}),
			);
		});

		Promise.all(docsData)
			.then(async attachments => {
				handleAddToExistingPMUploadData(attachments);
			})
			.catch(err => {
				console.log('handleExistingPMAttachmentsBtnPress() Error', err);
				setIsSubmitting(false);
			});
	};

	const handleAddToExistingPMUploadData = async attachments => {
		// Check if any document is selected for upload
		if (attachments.length === 0) {
			setIsSubmitting(false);
			setShowToast({
				isVisible: true,
				msg: 'Select atleast one document for upload',
				type: MessageBarType.warning,
			});
			return;
		}
		setIsLoading(true);

		const result = await handleUploadAttsToExistingPMItems(
			docDataForUpload[activeNode],
			attachments,
			userInfo,
			setShowToast,
			setIsLoading,
			selectedProject,
		);

		//Updating Parent's Attachment count
		handleParentCountUpdate(result.uploadedAttachmentsCount, () => fetchDataPagination(activeNode, { num: 0 }));
		setIsSubmitting(false);
	};

	const handlePMAttachmentsBtnPress = (attachments, mailInfo) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		setIsSubmitting(true);
		const promises = [];

		attachments.forEach(async result => {
			promises.push(
				new Promise(async (resolve, reject) => {
					const response = await saveAttachment(result);
					resolve({ ...result, ...response });
				}),
			);
		});

		Promise.all(promises)
			.then(maildData => {
				const attachmentInfo = [...attachmentData];

				const attachmentIndex = attachmentInfo.findIndex(item => item.messageId === mailInfo.id);

				if (attachmentIndex === -1) {
					setAttachmentData(prev =>
						[...maildData, ...prev].sort((a, b) => {
							if (a.name.toUpperCase() < b.name.toUpperCase()) {
								return -1;
							}
							if (a.name.toUpperCase() > b.name.toUpperCase()) {
								return 1;
							}
							return 0;
						}),
					);
					if (maildData.length) {
						setShowToast({
							isVisible: true,
							msg: `${maildData.length} attachments added`,
							type: MessageBarType.success,
						});
					} else {
						setShowToast({
							isVisible: true,
							msg: `Please select attachment(s).`,
							type: MessageBarType.warning,
						});
					}
					handleClickScroll();
				} else {
					let requiredAttachments = attachmentInfo;

					// ADDING LOGIC FOR EXISTING ATTACHMENTS

					const filteredAtachments = maildData.filter(e1 => requiredAttachments.every(e2 => e2.id !== e1.id));

					requiredAttachments = [...filteredAtachments, ...requiredAttachments];

					const sortedAttachment = requiredAttachments.sort((a, b) => {
						if (a.name.toUpperCase() < b.name.toUpperCase()) {
							return -1;
						}
						if (a.name.toUpperCase() > b.name.toUpperCase()) {
							return 1;
						}
						return 0;
					});

					setAttachmentData(sortedAttachment);

					if (maildData.length - filteredAtachments.length !== maildData.length) {
						setShowToast({
							isVisible: true,
							msg: `${filteredAtachments.length}  attachments added.${maildData.length - filteredAtachments.length
								} already present.`,
							type: MessageBarType.success,
						});
						handleClickScroll();
					} else {
						setShowToast({
							isVisible: true,
							msg: `All attachments already added.`,
							type: MessageBarType.success,
						});
					}
				}
				setIsSubmitting(false);
			})
			.catch(err => {
				console.log('handlePMAttachmentsBtnPress ERROR', err);
				setIsSubmitting(false);
			});
	};

	// Implemented to handle pagination, previous data will be replaced with new data on page change
	const handlePaginationModelChange = (num = 0) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		if (isNodeClicked.current) return;
		if (activeNode === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_TYPE) {
			fetchDataPagination(activeNode, { num, ImgsdtSourceUuid: nodeData.items[0]?.ImgsVUuid });
		} else {
			fetchDataPagination(activeNode, { num });
		}
	};

	// Implemented to handle sorting, we can have only one sort at one time
	const handleSortChange = (data, e) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		if (isNodeClicked.current || isLoading) return;

		const orderBy = handleSortFormat(data, activeNode);
		fetchDataPagination(activeNode, { num: 0, orderBy });
		// Handling sort model to store value for future reference
		setSortModel(prev => ({
			...prev,
			[activeNode]: data.length > 0 ? [{ field: data[0]?.field || '', sort: data[0]?.sort || '' }] : [],
		}));
	};

	// Implemented to handle filteration, we can only have one filter at one time and that too for specified columns
	const handleFilterationChange = (data, e) => {
		if (isNodeClicked.current) return;
		const filter = !data || data.items.length === 0 ? '' : filterFormatter(data?.items[0]);
		fetchDataPagination(activeNode, { num: 0, filter });
	};

	// Change size of Docs container for 'Create New Attachment' section
	const handleNewAttachmentContainerSize = useCallback(size => {
		setAttachmentSize(size);
	}, []);

	// Change size of Docs container for 'Add to Existing' section
	const handleExistedAttachmentContainerSize = useCallback(size => {
		setSplitSize(size);
	}, []);

	// visibility of the sorting menu (three dots) in the grid, allowing ascending or descending sorting
	const handleShowDisableColumnMenu = type => {
		switch (type) {
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_RFI:
				return false;
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_ISSUE:
				return false;
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_DAILY_JOURNAL:
				return false;
			case CMIC_TREE_NODE_TYPES.PROJECT_FOLDER_PCI:
				return false;
			default:
				return true;
		}
	};

	const DivLoader = useCallback(
		() => (
			<div style={loaderStyles}>
				<Spinner size={SpinnerSize.large} label="Almost there..." />
			</div>
		),
		[],
	);

	if (actionTypeData.type === ACTION_TYPE.NEW) {
		if (isLoading) return <DivLoader />;
		return (
			<Stack styles={parentStackStyles} style={{ padding: '0.1rem' }} tokens={HeaderMargin}>
				<CreateActions
					attachedData={attachmentData}
					selectedProjectData={nodeData?.items}
					selectedNodeType={actionTypeData.nodeType}
				/>
				<Divider
					style={{
						maxHeight: '0.1px',
						background:
							currentOfficeTheme.themeId === THEME_ID.BLACK ||
								currentOfficeTheme.themeId === THEME_ID.DARK_GREY
								? currentOfficeTheme.bodyForegroundColor
								: 'rgb(128,128,128, 0.4)',
					}}
				/>
				<ActionUploadAttachment
					btnLabel={isSubmitting ? 'ADDING FILES...' : 'ADD SELECTED FILES'}
					onBtnPress={handlePMAttachmentsBtnPress}
					isDisabled={isSubmitting}
					handleContainerSizeChange={handleNewAttachmentContainerSize}
					isAttachmentVisible={attachmentSize === SPLIT_SIZES.MIN ? false : true}
				/>
			</Stack>
		);
	}

	// Styles

	const StyledGridColumnMenu = styled(GridColumnMenu)(({ theme, ownerState }) => ({
		background: currentOfficeTheme.bodyBackgroundColor,
		color: currentOfficeTheme.bodyForegroundColor,
	}));

	const text_theme = handleTextTheme({ themeId: currentOfficeTheme.themeId });

	// Sub Components
	const CustomColumnMenuComponent = props => {
		const { hideMenu, colDef, color, ...other } = props;

		return (
			<StyledGridColumnMenu
				style={{
					'& .MuiListItemIconRoot': {
						color: currentOfficeTheme.bodyForegroundColor,
						background: currentOfficeTheme.bodyBackgroundColor,
					},
				}}
				hideMenu={hideMenu}
				colDef={colDef}
				ownerState={{ color }}
				{...other}
			/>
		);
	};

	const Pagination = ({ page, onPageChange, className }) => {
		return (
			<MuiPagination
				color="primary"
				aria-label="Pagingation"
				aria-current={page + 1}
				size="small"
				siblingCount={0}
				boundaryCount={1}
				showFirstButton
				showLastButton
				className={className}
				count={Math.ceil(nodeData.totalResults / pageSize)}
				page={page + 1}
				onChange={(event, newPage) => {
					onPageChange(event, newPage - 1);
				}}
				sx={{
					'& .MuiPagination-ul': {
						flexWrap: 'nowrap',
					},
					'.MuiPaginationItem-root': {
						color: currentOfficeTheme.bodyForegroundColor,
						minWidth: 24,
						height: 24,
						margin: '0 1px',
					},
					button: { color: currentOfficeTheme.bodyForegroundColor, fontFamily: baseFontFamilyStyle },
				}}
			/>
		);
	};

	const CustomPagination = props => {
		return (
			<GridPagination
				ActionsComponent={e => {
					return (
						<Pagination
							{...e}
							page={pageInfo[activeNode]?.num || 0}
							onPageChange={(e, page) => handlePaginationModelChange(page)}
						/>
					);
				}}
				{...props}
				sx={{
					width: '100%',
					maxWidth: '100%',
					overflow: 'hidden',
					'& .MuiTablePagination-toolbar': {
						width: '100%',
						maxWidth: '100%',
						paddingLeft: 0,
						paddingRight: 0,
					},
					'& .MuiTablePagination-actions': {
						flexShrink: 1,
						minWidth: 0,
						overflow: 'hidden',
					},
					'& .MuiPagination-root': {
						width: '100%',
						minWidth: 0,
						overflow: 'hidden',
					},
				}}
			/>
		);
	};

	const HeaderUI = () => (
		<Stack
			horizontal
			tokens={stackTokens}
			style={{
				borderBottom: isShowMetaPanelData(activeNode)
					? `0.5px solid ${currentOfficeTheme.bodyForegroundColor} `
					: '',
				paddingRight: '.3rem',
			}}
		>
			{!activeNode || activeNode === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT ? null : (
				<BorderIcon
					iconStyles={{
						color: COLORS.OUTLOOK_PRIMARY_BUTTON_COLOR,
						fontWeight: 'bold',
						fontSize: '18px',
					}}
					parentStyles={{ margin: '0 0 0.2rem 1px' }}
					iconName="Back"
					onClick={handleBackIconClick}
				/>
			)}
			<CustomBreadCrumbs isDisabled={isNodeClicked} parentStyle={{ width: '90%', marginTop: '3px' }} />
		</Stack>
	);

	if (isShowMetaPanelData(activeNode)) {
		return (
			<Stack styles={parentStackStyles} style={{ padding: '0.1rem' }} tokens={HeaderMargin}>
				<HeaderUI />
				<PMForm
					docData={docDataForUpload[activeNode]}
					splitSize={splitSize}
					isLoading={isLoading}
					isSubmitting={isSubmitting}
					currentOfficeTheme={currentOfficeTheme}
					handleBtnPress={handleExistingPMAttachmentsBtnPress}
				/>
			</Stack>
		);
	}

	return (
		<Stack
			styles={parentStackStyles}
			style={{
				padding: '0.1rem',
				height: '100%',
				...(activeNode === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT
					? { overflow: 'hidden' }
					: {}),
			}}
			tokens={HeaderMargin}
		>
			<HeaderUI />

			{isSearchPMVisible && (
				<SearchBox
					defaultValue={pageInfo[activeNode]?.search || ''}
					className={text_theme}
					placeholder={`Search ${breadcrumbs[0]?.label || ''} `}
					aria-label={`Search ${breadcrumbs[0]?.label} `}
					role="Search"
					autoComplete="off"
					disabled={isLoading}
					onChange={handlePMItemSearch}
					styles={searchInputStyles(currentOfficeTheme)}
				/>
			)}

			<DataGrid
				disableColumnMenu={handleShowDisableColumnMenu(activeNode)}
				disableColumnSelector={true}
				disableColumnFilter={true}
				loading={isLoading}
				pagination
				rows={rows}
				columns={columns}
				rowHeight={30}
				headerHeight={30}
				disableSelectionOnClick={isLoading}
				localeText={{ noRowsLabel: 'No records found' }}
				rowsPerPageOptions={[PAGE_LIMIT, DOC_PAGE_LIMIT]}
				paginationMode="server"
				sortingMode="server"
				filterMode="server"
				aria-label="Grid"
				rowCount={nodeData?.totalResults || 0}
				pageSizeOptions={[PAGE_LIMIT]}
				sortingOrder={['asc', 'desc', null]}
				slots={{
					columnMenu: CustomColumnMenuComponent,
					pagination: CustomPagination,
				}}
				sortModel={sortModel[activeNode] || []}
				paginationModel={{
					page: pageInfo[activeNode]?.num || 0,
					pageSize: pageSize,
				}}
				initialState={{
					pagination: {
						paginationModel: {
							pageSize: pageSize,
						},
					},
				}}
				onFilterModelChange={handleFilterationChange}
				onSortModelChange={handleSortChange}
				onRowClick={params => {
					handleNodeClick(params?.row?.ecmParentNodeType || params?.row?.Type || '', params?.row?.index || 0);
				}}
				style={{
					minHeight: '25vh',
					maxHeight: '100%',
				}}
				sx={dataGridStyling(
					currentOfficeTheme.bodyForegroundColor,
					currentOfficeTheme.bodyBackgroundColor,
					isBGClrDark() ? currentOfficeTheme.bodyForegroundColor : COLORS.OUTLOOK_BLACK_FONT,
					currentOfficeTheme,
				)}
				slotProps={dataGridsx(currentOfficeTheme)}
			/>

			{showAttachments && (
				<ActionUploadAttachment
					btnLabel={isSubmitting ? 'SAVING FILES' : 'SAVE SELECTED FILES'}
					onBtnPress={
						userInfo.actionRole === ACTION_ROLE.ECM
							? handleECMAttachmentsBtnPress
							: handleExistingPMAttachmentsBtnPress
					}
					isDisabled={isSubmitting}
					handleContainerSizeChange={handleExistedAttachmentContainerSize}
					isAttachmentVisible={splitSize === SPLIT_SIZES.MIN ? false : true}
				/>
			)}
		</Stack>
	);
};

export default ProjListAction;
